
import { useState, useEffect } from "react";
import { CustomerSelect } from "./transaction/CustomerSelect";
import { TransactionTypeSelect } from "./transaction/TransactionTypeSelect";
import { PaymentMethodSelect } from "./transaction/PaymentMethodSelect";
import { PaymentStatusSelect } from "./transaction/PaymentStatusSelect";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Calendar } from "lucide-react";
import { ExpenseFormFields } from "./transaction/ExpenseFormFields";
import { TransactionFormFields } from "./transaction/TransactionFormFields";
import { useTransactionSubmit } from "./transaction/TransactionSubmit";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface TransactionFormProps {
  initialData?: any;
  onSuccess?: () => void;
  onCancel?: () => void;
  isEditing?: boolean;
}

export function TransactionForm({ initialData, onSuccess, onCancel, isEditing = false }: TransactionFormProps) {
  const [transactionType, setTransactionType] = useState(initialData?.type || "entrada");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerName, setCustomerName] = useState("");
  const [amount, setAmount] = useState(initialData?.amount?.toString() || "");
  const [date, setDate] = useState(initialData?.due_date || new Date().toISOString().split('T')[0]);
  const [description, setDescription] = useState(initialData?.description || "");
  const [paymentMethod, setPaymentMethod] = useState(initialData?.payment_method || "pix");
  const [paymentStatus, setPaymentStatus] = useState(initialData?.payment_status || "pendente");
  const [name, setName] = useState(initialData?.name || "");
  const [category, setCategory] = useState(initialData?.category || "luz");
  const [isNewCustomerDialogOpen, setIsNewCustomerDialogOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [lastSubmissionTime, setLastSubmissionTime] = useState(0);
  const { toast } = useToast();
  const { handleSubmit, isSubmitting } = useTransactionSubmit();

  // Fetch customer data if initialData has customer_id
  useEffect(() => {
    const fetchCustomer = async () => {
      if (initialData?.customer_id) {
        const { data, error } = await supabase
          .from('customers')
          .select('*')
          .eq('id', initialData.customer_id)
          .single();
        
        if (data && !error) {
          setSelectedCustomer(data);
          setCustomerName(data.name);
        }
      }
    };
    
    if (isEditing) {
      fetchCustomer();
    }
  }, [initialData, isEditing]);

  const resetForm = () => {
    setTransactionType("entrada");
    setSelectedCustomer(null);
    setCustomerName("");
    setAmount("");
    setDate(new Date().toISOString().split('T')[0]);
    setDescription("");
    setPaymentMethod("pix");
    setPaymentStatus("pendente");
    setName("");
    setCategory("luz");
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent rapid consecutive submissions
    const now = Date.now();
    if (now - lastSubmissionTime < 3000 || isProcessing || isSubmitting) {
      toast({
        title: "Processando",
        description: "Por favor, aguarde um momento antes de submeter novamente",
        variant: "default",
      });
      return;
    }
    
    // Set submission flag
    setIsProcessing(true);
    setLastSubmissionTime(now);
    
    try {
      if (isEditing) {
        try {
          // Prepare data for update
          const updateData = {
            type: transactionType,
            customer_id: selectedCustomer?.id,
            name: transactionType === 'entrada' ? null : name,
            category: transactionType === 'despesas' ? category : null,
            description,
            amount: parseFloat(amount),
            payment_method: transactionType === 'despesas' ? 'n/a' : paymentMethod,
            payment_status: paymentStatus,
            due_date: date || null,
          };

          const { error } = await supabase
            .from('transactions')
            .update(updateData)
            .eq('id', initialData.id);

          if (error) throw error;

          toast({
            title: "Sucesso",
            description: "Transação atualizada com sucesso!",
            variant: "default",
          });

          if (onSuccess) onSuccess();
        } catch (error) {
          console.error('Error updating transaction:', error);
          toast({
            title: "Erro",
            description: "Erro ao atualizar transação. Tente novamente.",
            variant: "destructive",
          });
        }
      } else {
        // Use existing submit handler for new transactions
        const result = await handleSubmit({
          transactionType,
          selectedCustomer,
          amount,
          date,
          description,
          name,
          category,
          paymentMethod,
          paymentStatus,
          customerName,
          setIsNewCustomerDialogOpen,
          resetForm
        });

        if (result?.success && onSuccess) {
          onSuccess();
        }
      }
    } finally {
      // Release submission lock after a delay
      setTimeout(() => {
        setIsProcessing(false);
      }, 3000);
    }
  };

  const handleDelete = async () => {
    if (!initialData?.id) return;

    try {
      const { error } = await supabase
        .from('transactions')
        .delete()
        .eq('id', initialData.id);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Transação excluída com sucesso!",
        variant: "default",
      });

      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error deleting transaction:', error);
      toast({
        title: "Erro",
        description: "Erro ao excluir transação. Tente novamente.",
        variant: "destructive",
      });
    }
  };

  return (
    <form onSubmit={handleFormSubmit} className="max-w-4xl mx-auto">
      <div className="space-y-6">
        <TransactionTypeSelect
          value={transactionType}
          onChange={setTransactionType}
          disabled={isEditing}
        />

        {transactionType === "despesas" ? (
          <ExpenseFormFields
            name={name}
            setName={setName}
            category={category}
            setCategory={setCategory}
            amount={amount}
            setAmount={setAmount}
            date={date}
            setDate={setDate}
          />
        ) : (
          <TransactionFormFields
            transactionType={transactionType}
            selectedCustomer={selectedCustomer}
            setSelectedCustomer={setSelectedCustomer}
            customerName={customerName}
            setCustomerName={setCustomerName}
            description={description}
            setDescription={setDescription}
            amount={amount}
            setAmount={setAmount}
            date={date}
            setDate={setDate}
            paymentMethod={paymentMethod}
            setPaymentMethod={setPaymentMethod}
            paymentStatus={paymentStatus}
            setPaymentStatus={setPaymentStatus}
            name={name}
            setName={setName}
            category={category}
            setCategory={setCategory}
            onDelete={isEditing ? handleDelete : undefined}
          />
        )}

        <div className="pt-2 flex justify-end gap-4">
          {onCancel && (
            <Button
              type="button"
              onClick={onCancel}
              variant="outline"
              className="bg-transparent border border-gray-600 text-white hover:bg-gray-700"
            >
              Cancelar
            </Button>
          )}
          <Button
            type="submit"
            className="bg-[#0073B7] hover:bg-[#005a8f] text-white"
            disabled={isProcessing || isSubmitting}
          >
            {isProcessing || isSubmitting ? (
              <>
                <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2"></span>
                {isEditing ? "Atualizando..." : "Processando..."}
              </>
            ) : (
              isEditing ? "Atualizar Transação" : "Registrar Transação"
            )}
          </Button>
        </div>
      </div>
    </form>
  );
}
